// The client's mirror of the score-spec schema: which polymorphic `kind`s exist, which are
// actually implemented end-to-end, what each trigger *provides*, and what each document_set axis
// *requires*.
//
// EVERY TABLE HERE IS A MIRROR AND IT DECAYS. `app/factors/specs_loader.py` enables a kind by
// deleting an entry from its guard allowlist; on that day this file will still be hiding it. Three
// rules follow from that, and they are load-bearing:
//
//   1. Nothing here ever blocks Save or Lock. It counts problems and it locates them.
//   2. The pickers offer only what the engine accepts; a guard-blocked kind is hidden, not greyed
//      out with an explanation, because an operator cannot act on either.
//   3. The `{ }` raw pane accepts anything this file has never heard of.
//
// Transcribed from the ENGINE, not from prose: the tables below were checked against
// app/triggers/*.py and app/document_sets/documents/*.py, because the field-reference doc and the
// code do not agree on everything. The guard is __tests__/validate.test.js, which runs the two
// complete examples from score_spec_format.md AND the shipped production specs from
// app/factors/specs/ through validateDefinition and asserts zero errors — the doc examples alone
// were not enough, since none of them uses the document_available date anchor.
//
// The real fix is the backend's GET /score_specs/vocabulary; when it lands, this file becomes the
// fallback rather than the source.

export const STATUS = { SUPPORTED: 'supported', PLANNED: 'planned' };

// Kept out of every picker (see implementedKindsFor). The validator still needs these entries so a
// kind that arrives through the raw JSON pane or a seeded spec is recognised and explained rather
// than silently accepted — a spec carrying one saves cleanly and then cannot lock.
const NOT_ACCEPTED = 'The scoring engine does not accept this kind.';

export const TRIGGER_KINDS = [
  {
    kind: 'document_available',
    status: STATUS.SUPPORTED,
    // THREE fields, not two. `triggering_document_published_at` exists precisely so a documents
    // date_range can anchor on the trigger doc's date instead of on `now` — a now-relative window
    // would screen today for every historical occasion, which is look-ahead. Shipped specs
    // (jeff_margin_inflection_screen_v1, ray_ecosystem_spillover_v1) anchor on it.
    provides: ['triggering_company', 'triggering_document', 'triggering_document_published_at'],
    blurb: 'Fires when a newly-ingested document of an enumerated type becomes queryable.',
    caveat: null,
  },
  {
    kind: 'pre_earnings_release',
    status: STATUS.SUPPORTED,
    provides: ['triggering_company', 'target_release_date'],
    blurb: 'Calendar-driven — fires lead_time_days before a company’s earnings release.',
    caveat: null,
  },
  {
    kind: 'manual',
    status: STATUS.PLANNED,
    provides: [],
    blurb: 'Operator-fired one-off publish.',
    caveat: NOT_ACCEPTED,
  },
  { kind: 'scheduled', status: STATUS.PLANNED, provides: [], blurb: 'Per-spec cron.', caveat: NOT_ACCEPTED },
];

export const COMPANIES_KINDS = [
  {
    kind: 'triggering_company',
    status: STATUS.SUPPORTED,
    requires: ['triggering_company'],
    blurb: 'The one company from the trigger.',
    caveat: null,
  },
  { kind: 'explicit', status: STATUS.PLANNED, requires: [], blurb: 'A hardcoded company set.', caveat: NOT_ACCEPTED },
  { kind: 'universe', status: STATUS.PLANNED, requires: [], blurb: 'A named static universe.', caveat: NOT_ACCEPTED },
  {
    kind: 'peers_of_trigger',
    status: STATUS.PLANNED,
    requires: [],
    blurb: 'Peers of the trigger company, as a scoring axis.',
    caveat: NOT_ACCEPTED,
  },
];

export const DOCUMENTS_KINDS = [
  {
    kind: 'triggering_document',
    status: STATUS.SUPPORTED,
    requires: ['triggering_document'],
    blurb: 'The single document the trigger fired for.',
    caveat: null,
  },
  {
    kind: 'related_filter',
    status: STATUS.SUPPORTED,
    // The engine declares REQUIRES = frozenset(). Its real cross-field rule is the date_range
    // anchor check, which depends on the spec's anchor string rather than on a static field, so it
    // is validated separately. Claiming a requirement here would disable the card on a trigger the
    // loader accepts.
    requires: [],
    blurb: 'The company’s related companies’ latest N per doctype in a date window.',
    // Downgraded to EXPERIMENTAL on Aug 27 (Change 7). The old copy recommended this as the
    // preferred peer/read-through path; that recommendation is now wrong, and the reason is not a
    // performance one — the related-company dataset is not lookahead-safe, so a backfill can read
    // relationships that did not exist on the occasion date. That is silent, and it invalidates the
    // scores rather than failing the run.
    caveat:
      'EXPERIMENTAL — not production-ready. Scores the trigger company from OTHER companies’ documents, and the related-company dataset is not currently lookahead-safe: a backfill can read relationships that did not exist yet on the occasion date.',
  },
  {
    kind: 'filter',
    status: STATUS.SUPPORTED,
    requires: ['triggering_document'],
    // Promoted to fully operational (Change 7): the live and backfill paths now pin documents
    // identically, on the same (doc_ids, source_published_at). It is the production path.
    blurb:
      'The company’s own latest N per doctype, as of the occasion date. Fully operational — live and backfill pin the same documents.',
    caveat: null,
  },
  {
    kind: 'screen',
    status: STATUS.SUPPORTED,
    // REQUIRES = frozenset() in the engine, same reasoning as related_filter.
    requires: [],
    blurb: 'Broad semantic retrieval across the corpus in a date window.',
    caveat:
      'IN DEVELOPMENT and slow. Broad retrieval is imprecise, and restrict_to_companies does not actually bound it — the search stays corpus-wide.',
  },
];

export const FANOUT_KINDS = [
  { kind: 'per_company', status: STATUS.SUPPORTED, requires: [], blurb: 'One event per company.', caveat: null },
  {
    kind: 'batch_all',
    status: STATUS.PLANNED,
    requires: [],
    blurb: 'One event covering all companies.',
    caveat: NOT_ACCEPTED,
  },
  {
    kind: 'batch_n',
    status: STATUS.PLANNED,
    requires: [],
    blurb: 'Chunk companies into batches of N.',
    caveat: NOT_ACCEPTED,
  },
];

// related_filter.companies / screen.restrict_to_companies share this registry.
export const COMPANY_SCOPE_KINDS = [
  {
    kind: 'peers_of_trigger',
    status: STATUS.SUPPORTED,
    requires: [],
    blurb: 'Follow the company_relationships graph.',
    caveat: null,
  },
];

const AXES = {
  trigger: TRIGGER_KINDS,
  companies: COMPANIES_KINDS,
  documents: DOCUMENTS_KINDS,
  fanout: FANOUT_KINDS,
  company_scope: COMPANY_SCOPE_KINDS,
};

export const kindsFor = (axis) => AXES[axis] || [];

export const kindEntry = (axis, kind) => kindsFor(axis).find((k) => k.kind === kind) || null;

// What the PICKERS offer. Only kinds the engine actually accepts — an unavailable option teaches
// nothing and an operator cannot act on it, so it is not shown at all.
export const implementedKindsFor = (axis) => kindsFor(axis).filter((k) => k.status === STATUS.SUPPORTED);

// The payload fields a trigger stamps onto its events. Unknown kinds provide nothing, which is
// deliberately indistinguishable from "provides nothing" — the validator words it as unknown.
export const providesFor = (triggerKind) => {
  const entry = kindEntry('trigger', triggerKind);
  return entry ? entry.provides : [];
};

export const requiresFor = (axis, kind) => {
  const entry = kindEntry(axis, kind);
  return entry ? entry.requires || [] : [];
};

export const isSupported = (axis, kind) => {
  const entry = kindEntry(axis, kind);
  return !!entry && entry.status === STATUS.SUPPORTED;
};

export const isKnown = (axis, kind) => !!kindEntry(axis, kind);

// ---- fixed vocabularies ----

export const RELATIONSHIP_KINDS = ['peer', 'customer', 'supplier'];
export const REASONING_EFFORTS = ['minimal', 'low', 'medium', 'high'];
export const CONTEXT_SETTING_KEYS = [
  'use_web_social_context',
  'use_fundamentals_context',
  'use_analyst_estimates_context',
  'use_institutional_holdings_context',
  'use_stock_price_return_context',
  'use_inferred_metadata',
  'use_fye_context',
  'use_formula_dependency_context',
  'use_raw_spreadsheet_context',
  'augment_abbreviations',
  'include_hidden_sheets_in_context',
];
export const CONTEXT_SETTING_VALUES = ['yes', 'no', 'auto'];

// `source_kind` is a REAL, accepted, optional key, not an unknown one: _FactorSpecJSON declares it
// with a default (specs_loader.py:385), so a spec that omits it — all 29 seeds do — and a spec that
// sets it to 'score_spec' both compile and lock. Listing it here stops the unknown-key rule from
// calling a legal key illegal and offering to remove it.
//
// Listing it is only half the job. Its VALUE is checked in validate.js, because the key selects
// WHICH schema validates the row: allowing the key without checking what it says would swap a false
// positive for a false negative, and 'derived_feed' is a real kind this editor can never lock.
export const TOP_LEVEL_KEYS = [
  'source_kind',
  'score_spec_id',
  'version',
  'description',
  'notes',
  'enabled_in',
  'trigger',
  'document_set',
  'score_gen',
  'output_extraction',
];

// The two kinds a row in the score_specs store can declare (specs_loader.py:348-350). This editor
// authors the first only. `source_kind_of` reads the key off the RAW row before validation
// (specs_loader.py:718-728) and rejects a value outside this list; compile_spec then refuses even a
// legitimate `derived_feed` up front (specs_loader.py:771-776), because a derived feed carries
// feed_name / builder instead of scoring blocks. Either way the lenient create/update tier saves it
// — the refusal lands at lock.
export const SOURCE_KINDS = ['score_spec', 'derived_feed'];
export const SCORE_SPEC_SOURCE_KIND = 'score_spec';

// The authored sub-objects, in the order the four question panels present them.
export const SUB_KEYS = ['trigger', 'document_set', 'score_gen', 'output_extraction'];

// OpenAI strict-mode subset, enforced by the loader on output_schema. `enum` was added in PR #457
// and is genuinely grammar-enforced by the model, so a categorical score can be constrained rather
// than merely asked for. Everything else — anyOf/oneOf/$ref/const/pattern/minimum/… — is still
// rejected, so a numeric RANGE still belongs in the prompt.
export const SCHEMA_ALLOWED_KEYS = [
  'type',
  'properties',
  'required',
  'additionalProperties',
  'items',
  'description',
  'enum',
];
export const SCHEMA_ALLOWED_TYPES = ['object', 'array', 'string', 'number', 'integer', 'boolean', 'null'];

export const SPEC_ID_REGEX = /^[a-z][a-z0-9_]*_v(\d+)$/;
// `__` is banned in a spec id as well, and the regex above cannot express it — it allows `_`, so a
// double underscore passes. It is a SEPARATE check (see validate.js) for the same reason it is banned
// in a score name: the published feed is `score_spec_<id>[__<name>]`, and a `__` inside the id makes
// that name ambiguous to reverse-resolve back to a spec + score.
//
// The reserved point-in-time row columns. Two different rules ride on this one list:
//   * a `scores[].name` duplicating one is a LOAD error — the score column would clash with the row
//     column in the export;
//   * a spec-level `extra_columns` name duplicating one is TOLERATED and then silently dropped from
//     the payload (on the publish path, in snapshot.build_frames, and at view time) so it cannot
//     overwrite the row column. Silent is the problem, which is why it is worth saying out loud.
export const RESERVED_FEED_COLUMNS = [
  'ticker',
  'company_id',
  'company_name',
  'utc_knowable_at',
  'sector',
  'market_cap',
];
// A free-form `scores[].feed_name` (PR #459, Change 3). The value is the SUFFIX only — the system
// prepends `score_spec_` — which is what lets a v4 re-cut keep publishing to v3's feed.
//
// Its format check lives in the STRICT tier, not the lenient create/PATCH one, so a malformed value
// saves cleanly and only fails at preview_score or lock. That is why it is checked as typed here:
// this is the only pre-lock signal there is. `__` is excluded because it is the separator the
// derived multi-score name uses, and `<N>runs` for the same reason a score name reserves it.
export const FEED_NAME_REGEX = /^[a-z0-9_]+$/;
export const FEED_NAME_SEPARATOR = /__/;
// Score names are permanent feed/S3/NATS tokens. `^\d+runs$` collides with the __<N>runs
// multi-run feed suffix and is therefore reserved.
export const SCORE_NAME_REGEX = /^[a-z0-9_]+$/;
export const RESERVED_SCORE_NAME_REGEX = /^\d+runs$/;

// The Jinja namespace the engine builds for a run, keyed by DOCUMENTS kind. Transcribed from
// app/fapi_event_workers/factor_runner.py:259-310 (`_build_template_context`), which switches on the
// event payload's selection block — document / screen / filter / related — and returns a DIFFERENT
// dict for each.
//
// This is not one set with optional extras. Four of the seven names a `triggering_document` spec
// gets (doc_id, doc_type, published_at, available_to_query_at) do not exist for the other three
// kinds, and five names those kinds do get (doc_types, as_of, date_from, date_to,
// relationship_kinds) do not exist for a document spec.
//
// Getting it wrong is not cosmetic. The engine renders with jinja2.StrictUndefined
// (app/prompt_templating.py:32), so a variable outside the set does NOT render empty — it raises,
// and factor_runner turns that into PermanentError('template_render_failed')
// (factor_runner.py:998-1004). Every unit of the run dies, and a permanent error does not retry.
//
// score_spec_format.md also lists `include_self` for related_filter. The engine does not build it
// (factor_runner.py:298-308) — the prose is wrong, and a prompt using it dies like any other
// undefined name. Transcribed from the engine, as the header of this file requires.
export const RUN_JINJA_VARIABLES = {
  triggering_document: [
    'company_id',
    'ticker',
    'company_name',
    'doc_id',
    'doc_type',
    'published_at',
    'available_to_query_at',
  ],
  screen: ['company_id', 'ticker', 'company_name', 'doc_types', 'date_from', 'date_to'],
  filter: ['company_id', 'ticker', 'company_name', 'doc_types', 'as_of'],
  related_filter: ['company_id', 'ticker', 'company_name', 'doc_types', 'date_from', 'date_to', 'relationship_kinds'],
};

// preview_score builds ONE document-shaped context for EVERY spec, whatever its documents kind
// (app/api/score_specs.py:457-465). A preview is therefore only a faithful rehearsal for a
// `triggering_document` spec; for the other three it misleads in BOTH directions.
//
// UNVERIFIED since fapi #492 (2026-09-06). That PR made preview_score resolve the spec's own
// trigger over a universe and window rather than taking hand-fed doc ids, and the response now
// reports the documents that actually fed each score. If it also renders the real per-kind context,
// this constant and every sentence built on it are stale. The claim is repeated in three other
// shipped surfaces — PromptEditor.js (the `previewLies` warning), docs/SCORE_SPEC_EDITING_ACTIONS.md
// and __tests__/jinjaVars.test.js — and NONE of them should be changed on inference. Confirm with
// the fapi team first; the ask is recorded in plans/preview-score-universe-window/PLAN.md. A `filter` prompt
// using {{published_at}} previews green and then kills every unit in production, and a `filter`
// prompt correctly using {{as_of}} comes back 502 `missing template variable` from a preview that
// was never going to have it.
export const PREVIEW_JINJA_VARIABLES = RUN_JINJA_VARIABLES.triggering_document;

// null — not [] — for a kind this file has never heard of: an unknown kind must not turn every
// variable in the prompt into a warning. hasOwnProperty because the raw JSON pane accepts any
// string as a kind, and `constructor` would otherwise resolve up the prototype chain.
export const runVariablesFor = (documentsKind) =>
  Object.prototype.hasOwnProperty.call(RUN_JINJA_VARIABLES, documentsKind) ? RUN_JINJA_VARIABLES[documentsKind] : null;

// ---- date_range token grammar ----

export const DATE_TOKEN = {
  NOW: /^now$/,
  NOW_OFFSET: /^now[+-]\d+d$/,
  ANCHOR: /^T$/,
  ANCHOR_OFFSET: /^T[+-]\d+d$/,
  ABSOLUTE: /^\d{4}-\d{2}-\d{2}$/,
};

export const DATE_MODES = { NOW: 'now', ANCHORED: 'anchored', ABSOLUTE: 'absolute' };

export const isAnchorToken = (token) =>
  DATE_TOKEN.ANCHOR.test(String(token)) || DATE_TOKEN.ANCHOR_OFFSET.test(String(token));
export const isNowToken = (token) => DATE_TOKEN.NOW.test(String(token)) || DATE_TOKEN.NOW_OFFSET.test(String(token));
export const isAbsoluteToken = (token) => DATE_TOKEN.ABSOLUTE.test(String(token));
export const isValidDateToken = (token) => isAnchorToken(token) || isNowToken(token) || isAbsoluteToken(token);

// The mode a date_range is in, derived from its tokens rather than stored. This derivation is what
// makes the anchor rules structural: `now` mode never emits an anchor key, anchored mode always
// does, and anchored mode is simply not offered when the trigger provides no date field.
export const dateModeOf = (range) => {
  if (!range) return DATE_MODES.NOW;
  if (isAnchorToken(range.from) || isAnchorToken(range.to)) return DATE_MODES.ANCHORED;
  if (isAbsoluteToken(range.from) || isAbsoluteToken(range.to)) return DATE_MODES.ABSOLUTE;
  return DATE_MODES.NOW;
};

// The document_set.documents kinds that carry a date_range.
export const KINDS_WITH_DATE_RANGE = ['screen', 'related_filter'];
// The document_set.documents kinds whose doc_types is a RULE list ({doc_type, latest_n?}).
// `screen`'s doc_types is a plain list of strings — a different shape under the same key, which is
// why switching between them has to re-seed rather than carry the value over.
export const KINDS_WITH_DOC_TYPE_RULES = ['filter', 'related_filter'];
export const KINDS_WITH_DOC_TYPE_STRINGS = ['screen'];

// Exactly which params each documents kind accepts, mirroring each resolver's `Params` model.
// Every one of those models is `extra: "forbid"`, and the loader passes every non-`kind` key
// straight into it — so a param left behind from a previously-selected kind is not inert, it makes
// the spec uncompilable. This table drives both the cleanup on a kind change and the unknown-param
// check in validate.js.
export const DOCUMENT_KIND_PARAMS = {
  triggering_document: [],
  filter: ['doc_types'],
  related_filter: ['doc_types', 'date_range', 'companies'],
  screen: ['doc_types', 'date_range', 'restrict_to_companies'],
};

// A trigger's Provides fields that are dates, and so are the only sensible things for a window to
// anchor on. The loader only checks membership in Provides, so anchoring on a company ref compiles
// — it is just meaningless. The form offers these first rather than whatever came first in the list.
export const DATE_PROVIDES = ['target_release_date', 'triggering_document_published_at'];

export const dateProvidesFor = (triggerKind) => providesFor(triggerKind).filter((f) => DATE_PROVIDES.indexOf(f) > -1);
